interface AnswerMapValue {
  answerKeys?: string[];
  selectedKeys?: string[];
  answerValues?: unknown[];
  answerLabels?: string[];
  notesForKeys?: Record<string, string | undefined>;
  itemAnswers?: Record<string, Record<string, AnswerMapValue>>;
}

interface TreeStateLike {
  treeKey?: string;
  answerMap?: Record<string, AnswerMapValue>;
}

interface ReportStateLike {
  treeStates?: TreeStateLike[];
  globalAnswerMap?: Record<string, unknown>;
}

const medicalAbbreviations: Record<string, string> = {
  Abd: 'abdomen',
  'Abd Whole': 'whole abdomen',
  Chest: 'chest',
  'Chest Rt Upper': 'right upper chest',
  'Chest Lt Upper': 'left upper chest',
  'Chest Rt Lower': 'right lower chest',
  'Chest Lt Lower': 'left lower chest',
  Rt: 'right',
  Lt: 'left',
  Upper: 'upper',
  Lower: 'lower',
  'Abd Pain': 'abdominal pain',
  'Chest Pain': 'chest pain',
  'Groin Pain': 'groin pain',
  'Head Pain': 'headache',
  'Back Pain': 'back pain',
  Onset: 'onset',
  Duration: 'duration',
  Frequency: 'frequency',
  Nature: 'nature',
  Context: 'context',
  Progression: 'progression',
  PMH: 'past medical history',
  PMHx: 'past medical history',
  CC: 'chief complaint',
  HPI: 'history of present illness',
};

function humanizeMachineKey(value: string): string {
  return value
    .replace(/^(o|sx|q)-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
}

function isMachineKey(value: string): boolean {
  return /^(o|sx|q)-[a-z0-9-]+$/i.test(value.trim());
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
}

/**
 * Normalize raw values from itemAnswers into AnswerMapValue format.
 * itemAnswers can store values as direct primitives (e.g., pain NRS: 4),
 * onset metadata objects ({ unit, duration, calculatedDate }), or proper
 * AnswerMapValue objects ({ answerKeys, selectedKeys, answerValues }).
 */
function normalizeToAnswerMapValue(value: unknown): AnswerMapValue {
  if (value == null) return {};

  // Direct primitive (number, string, boolean)
  if (typeof value !== 'object') {
    return { answerValues: [String(value)] };
  }

  // Array
  if (Array.isArray(value)) {
    return { answerValues: value.map((v) => String(v)) };
  }

  const obj = value as Record<string, unknown>;

  // Onset metadata: { unit, duration, calculatedDate }
  if (
    ('calculatedDate' in obj || ('unit' in obj && 'duration' in obj)) &&
    !('answerKeys' in obj) &&
    !('selectedKeys' in obj) &&
    !('answerValues' in obj)
  ) {
    const calculatedDate = typeof obj.calculatedDate === 'string' ? obj.calculatedDate : '';
    const unit = typeof obj.unit === 'string' ? obj.unit : '';
    const duration = typeof obj.duration === 'number' ? obj.duration : NaN;

    if (Number.isFinite(duration) && duration > 0 && unit) {
      return { answerValues: [`${duration} ${unit}${duration > 1 ? 's' : ''} ago`] };
    }
    if (calculatedDate) {
      return { answerValues: [calculatedDate] };
    }
    return {};
  }

  return value as AnswerMapValue;
}

function normalizeBinaryValue(value: string): 'yes' | 'no' | null {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'yes' || normalized === 'o-yes') return 'yes';
  if (normalized === 'no' || normalized === 'o-no') return 'no';
  return null;
}

function getBestAnswerText(answer: AnswerMapValue | undefined): string {
  const optionKeys = getOptionKeys(answer);
  const optionText =
    optionKeys.length > 0 ? getOptionText(answer, optionKeys[0], 0) : '';
  const value = normalizeText(answer?.answerValues?.[0]);
  const label = normalizeText(answer?.answerLabels?.[0]);

  if (optionText && value) {
    const optionBinary = normalizeBinaryValue(optionText);
    const valueBinary = normalizeBinaryValue(value);
    // If stored answerValue is stale/contradictory with selected option key,
    // trust the selected option key to avoid Yes/No inversion in prompt data.
    if (optionBinary && valueBinary && optionBinary !== valueBinary) {
      return optionText;
    }
  }

  if (label) {
    return label;
  }

  if (value) {
    return isMachineKey(value) ? humanizeMachineKey(value) : value;
  }

  if (optionText) {
    return optionText;
  }

  return '';
}

function toQuestionText(questionKey: string): string {
  return questionKey
    .replace('q-', '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
}

function getOptionKeys(answer: AnswerMapValue | undefined): string[] {
  const keys = [
    ...(answer?.answerKeys || []),
    ...(answer?.selectedKeys || []),
  ];

  return Array.from(new Set(keys.map((key) => normalizeText(key)).filter(Boolean)));
}

function getOptionText(answer: AnswerMapValue | undefined, key: string, index: number): string {
  const labelByIndex = normalizeText(answer?.answerLabels?.[index]);
  if (labelByIndex) return labelByIndex;
  return humanizeMachineKey(key);
}

function convertMedicalAbbreviation(text: string): string {
  if (medicalAbbreviations[text]) {
    return medicalAbbreviations[text];
  }

  const found = Object.entries(medicalAbbreviations).find(([abbrev]) =>
    text.includes(abbrev),
  );
  if (found) {
    const [abbrev, full] = found;
    return text.replace(abbrev, full);
  }

  return text;
}

function humanizeDateDifference(dateStr: string): string {
  if (!dateStr) return dateStr;

  const now = new Date();
  const ymdhmMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
  const ymdhMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2})$/);
  const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  let date: Date | null = null;
  if (ymdhmMatch) {
    date = new Date(
      Number(ymdhmMatch[1]),
      Number(ymdhmMatch[2]) - 1,
      Number(ymdhmMatch[3]),
      Number(ymdhmMatch[4]),
      Number(ymdhmMatch[5]),
    );
  } else if (ymdhMatch) {
    date = new Date(
      Number(ymdhMatch[1]),
      Number(ymdhMatch[2]) - 1,
      Number(ymdhMatch[3]),
      Number(ymdhMatch[4]),
      0,
    );
  } else if (ymdMatch) {
    date = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
  }

  if (!date) return dateStr;

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 60000) return 'Just now';
  if (diffMs < 3600000) {
    const min = Math.floor(diffMs / 60000);
    return `${min} minute${min === 1 ? '' : 's'} ago`;
  }
  if (diffMs < 86400000) {
    const hours = Math.floor(diffMs / 3600000);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

type DescriptiveSection = 'hpi' | 'pmh' | 'medication' | 'allergy';

export interface LabelCatalogEntry {
  answerLabels: Record<string, string>;
  answerLabelsByQuestion: Record<string, Record<string, string>>;
}

export interface ExtractDescriptiveOptions {
  /** Maps symptom tree keys (e.g. "sx-headache") to localised display names */
  symptomLabelByKey?: Record<string, string>;
  /** Catalog of answer labels keyed by tree key – used to resolve localised
   *  answer text when the answer's own answerLabels array is empty. */
  labelCatalog?: Record<string, LabelCatalogEntry>;
}

/**
 * If the answer has no answerLabels populated, try to fill them from the
 * label catalog using the answer's answerKeys + the catalog for the given
 * tree / question.
 */
function enrichAnswerFromCatalog(
  answer: AnswerMapValue,
  treeKey: string,
  questionKey: string,
  catalog?: Record<string, LabelCatalogEntry>,
): AnswerMapValue {
  if (!catalog) return answer;
  if (!answer.answerKeys || answer.answerKeys.length === 0) return answer;
  if (answer.answerLabels && answer.answerLabels.some((l) => !!l)) return answer;

  const entry = catalog[treeKey];
  if (!entry) return answer;

  const labels = answer.answerKeys.map((key) => {
    const qLabels = entry.answerLabelsByQuestion[questionKey];
    return qLabels?.[key] || entry.answerLabels[key] || '';
  });

  return labels.some((l) => l) ? { ...answer, answerLabels: labels } : answer;
}

export function resolveSection(treeKeyRaw: string, questionKeyRaw: string): DescriptiveSection {
  const treeKey = treeKeyRaw.toLowerCase();
  const questionKey = questionKeyRaw.toLowerCase();

  if (questionKey.includes('allergy') || treeKey.includes('allergy')) {
    return 'allergy';
  }

  if (
    questionKey.includes('medication') ||
    questionKey.includes('meds') ||
    questionKey.includes('med-') ||
    questionKey.includes('-med') ||
    questionKey.includes('pta') ||
    questionKey.includes('dhx') ||
    questionKey.includes('contraceptive') ||
    treeKey.includes('medication')
  ) {
    return 'medication';
  }

  if (
    questionKey.includes('pmh') ||
    questionKey.includes('pmhx') ||
    questionKey.includes('history') ||
    treeKey.includes('pmh') ||
    treeKey.includes('history')
  ) {
    return 'pmh';
  }

  return 'hpi';
}

export function extractDescriptiveSections(reportState: ReportStateLike, options?: ExtractDescriptiveOptions) {
  const hpiData: string[] = [];
  const allergyData: string[] = [];
  const pmhData: string[] = [];
  const medicationData: string[] = [];

  const treeStatesInfo = reportState.treeStates || [];
  const globalAnswerMap = reportState.globalAnswerMap || {};

  const pushSymptomHpiLine = (symptomTreeKey: string, questionKey: string, rawAnswer: AnswerMapValue) => {
    const symptomName = options?.symptomLabelByKey?.[symptomTreeKey] ||
      symptomTreeKey
        .replace('sx-', '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (l: string) => l.toUpperCase());

    // Enrich answer with localised labels from catalog when available
    const answer = enrichAnswerFromCatalog(rawAnswer, symptomTreeKey, questionKey, options?.labelCatalog);

    const answerTexts: string[] = [];
    const bestText = getBestAnswerText(answer);
    if (bestText) {
      answerTexts.push(bestText);
    }
    getOptionKeys(answer).forEach((key, index) => {
      answerTexts.push(getOptionText(answer, key, index));
    });

    Array.from(new Set(answerTexts.filter(Boolean))).forEach((rawText) => {
      let questionText = toQuestionText(questionKey);

      if (questionKey === 'q-onset-datetime') questionText = 'Onset';
      else if (questionKey === 'q-sx-count') questionText = 'Frequency';
      else if (questionKey === 'q-sx-duration') questionText = 'Duration';
      else if (questionKey === 'q-sx-continuous-intermittent') questionText = 'Nature';
      else if (questionKey === 'q-chest-location') questionText = 'Location';
      else if (questionKey === 'q-abrupt-gradual') questionText = 'Onset Type';
      else if (questionKey === 'q-chest-event') questionText = 'Context';
      else if (questionKey === 'q-sx-aggravation') questionText = 'Progression';

      const convertedSymptomName = convertMedicalAbbreviation(symptomName);
      const convertedQuestionText = convertMedicalAbbreviation(questionText);
      let convertedValue = convertMedicalAbbreviation(rawText);

      if (questionText === 'Onset' || convertedQuestionText.includes('Datetime')) {
        if (/^\d{4}-\d{2}-\d{2}/.test(convertedValue)) {
          convertedValue = humanizeDateDifference(convertedValue);
        }
      }

      hpiData.push(`${convertedSymptomName} - ${convertedQuestionText}: ${convertedValue}`);
    });
  };

  treeStatesInfo.forEach((treeState) => {
    if (!treeState?.answerMap) return;

    // Skip pmhx-screen in the general loop — it has a dedicated section below
    // that produces properly labelled entries (e.g. "Medication - Medication Prior to Admission").
    const isPmhxScreen = treeState.treeKey === 'pmhx-screen';

    if (!isPmhxScreen) {
    Object.entries(treeState.answerMap).forEach(([questionKey, rawAnswer]) => {
      const treeKey = treeState.treeKey || 'unknown';
      const section = resolveSection(treeKey, questionKey);
      const answer = enrichAnswerFromCatalog(rawAnswer, treeKey, questionKey, options?.labelCatalog);

      if (answer?.answerValues && answer.answerValues.length > 0) {
        const value = getBestAnswerText(answer);
        if (value.trim()) {
          if (section === 'allergy') {
            allergyData.push(`Allergy: ${value}`);
          } else if (section === 'pmh') {
            pmhData.push(`PMH: ${value}`);
          } else if (section === 'medication') {
            medicationData.push(`Medication: ${value}`);
          } else if (
            treeKey.toLowerCase().includes('injury') ||
            treeKey.toLowerCase().includes('pain') ||
            treeKey.toLowerCase().includes('symptom')
          ) {
            hpiData.push(`${treeKey}: ${value}`);
          }
        }
      }

      const optionKeys = getOptionKeys(answer);
      if (optionKeys.length > 0 && !treeKey.startsWith('sx-')) {
        optionKeys.forEach((key: string, index: number) => {
          const noteForKey = answer?.notesForKeys?.[key];
          const hasNote = noteForKey && typeof noteForKey === 'string' && noteForKey.trim();
          const questionText = toQuestionText(questionKey);
          const optionText = getOptionText(answer, key, index);

          if (key.startsWith('o-')) {
            if (section === 'allergy') {
              allergyData.push(
                hasNote
                  ? `Allergy - ${questionText}: ${optionText} (${noteForKey})`
                  : `Allergy - ${questionText}: ${optionText}`,
              );
            } else if (section === 'pmh') {
              pmhData.push(
                hasNote
                  ? `PMH - ${questionText}: ${optionText} (${noteForKey})`
                  : `PMH - ${questionText}: ${optionText}`,
              );
            } else if (section === 'medication') {
              medicationData.push(
                hasNote
                  ? `Medication - ${questionText}: ${optionText} (${noteForKey})`
                  : `Medication - ${questionText}: ${optionText}`,
              );
            } else {
              hpiData.push(
                hasNote
                  ? `${questionText}: ${optionText} (${noteForKey})`
                  : `${questionText}: ${optionText}`,
              );
            }
          }

          if (key.startsWith('sx-')) {
            if (treeKey.toLowerCase().includes('allergy')) {
              allergyData.push(
                hasNote
                  ? `Allergy Reaction - ${questionText}: ${optionText} (${noteForKey})`
                  : `Allergy Reaction - ${questionText}: ${optionText}`,
              );
            } else {
              hpiData.push(
                hasNote
                  ? `${questionText}: ${optionText} (${noteForKey})`
                  : `${questionText}: ${optionText}`,
              );
            }
          }
        });
      }

      if (answer?.itemAnswers) {
        Object.entries(answer.itemAnswers).forEach(([symptomKey, nestedAnswerMap]) => {
          if (!symptomKey.startsWith('sx-') || !nestedAnswerMap) return;
          Object.entries(nestedAnswerMap).forEach(([nestedQuestionKey, nestedAnswer]) => {
            pushSymptomHpiLine(symptomKey, nestedQuestionKey, normalizeToAnswerMapValue(nestedAnswer));
          });
        });
      }
    });
    } // end if (!isPmhxScreen)

    if (treeState.treeKey === 'pmhx-screen' && treeState.answerMap) {
      Object.entries(treeState.answerMap).forEach(([questionKey, answer]) => {
        let questionText = questionKey
          .replace('q-', '')
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());

        if (questionKey === 'q-relevant-history') questionText = 'Relevant History';
        else if (questionKey.includes('q-medication-pta-time')) questionText = 'Medication Prior to Admission Time';
        else if (questionKey.includes('q-medication-pta')) questionText = 'Medication Prior to Admission';

        const sectionForQuestion = resolveSection(treeState.treeKey || 'pmhx-screen', questionKey);

        if (answer?.answerValues && answer.answerValues.length > 0) {
          const value = getBestAnswerText(answer);
          if (value.trim()) {
            if (sectionForQuestion === 'allergy') {
              allergyData.push(`Allergy - ${questionText}: ${value}`);
            } else if (sectionForQuestion === 'medication') {
              medicationData.push(`Medication - ${questionText}: ${value}`);
            } else {
              pmhData.push(`PMH - ${questionText}: ${value}`);
            }
          }
        }

        if (answer?.answerKeys && answer.answerKeys.length > 0) {
          answer.answerKeys.forEach((key: string, index: number) => {
            if (key.startsWith('o-')) {
              const labelByIndex = normalizeText(answer.answerLabels?.[index]);
              const optionText = labelByIndex || humanizeMachineKey(key);
              if (sectionForQuestion === 'allergy') {
                allergyData.push(`Allergy - ${questionText}: ${optionText}`);
              } else if (sectionForQuestion === 'medication') {
                medicationData.push(`Medication - ${questionText}: ${optionText}`);
              } else {
                pmhData.push(`PMH - ${questionText}: ${optionText}`);
              }
            }
          });
        }
      });
    }

    if (treeState.treeKey && treeState.treeKey.startsWith('sx-')) {
      if (treeState.answerMap && Object.keys(treeState.answerMap).length > 0) {
        Object.entries(treeState.answerMap).forEach(([questionKey, answer]) => {
          pushSymptomHpiLine(treeState.treeKey as string, questionKey, normalizeToAnswerMapValue(answer));
        });
      } else {
        // Symptom was selected but has no detailed answers - still include the symptom name
        const symptomName = humanizeMachineKey(treeState.treeKey.replace('sx-', ''));
        hpiData.push(`${convertMedicalAbbreviation(symptomName)} (reported, no details)`);
      }
    }
  });

  // Collect all question keys that actually exist in treeStates.
  // Used below to prevent orphaned HPI answers (e.g. NRS from a previous survey version)
  // from leaking into the prompt via globalAnswerMap.
  const treeStateQuestionKeys = new Set<string>();
  treeStatesInfo.forEach((ts) => {
    if (!ts.answerMap) return;
    Object.keys(ts.answerMap).forEach((k) => treeStateQuestionKeys.add(k));
  });

  // Some surveys store PMHx/Medication-related answers in globalAnswerMap only.
  // Process them with the same section resolver so they are not omitted.
  // HPI-section questions are ONLY included when they also exist in treeStates
  // to prevent stale data (e.g. NRS scores) from appearing without a matching symptom tree.
  Object.entries(globalAnswerMap).forEach(([questionKey, answerRaw]) => {
    const answer = (answerRaw || {}) as AnswerMapValue;
    const section = resolveSection('global', questionKey);

    // Skip HPI questions that are not present in any treeState
    if (section === 'hpi' && !treeStateQuestionKeys.has(questionKey)) return;

    const questionText = toQuestionText(questionKey);

    if (answer?.answerValues && answer.answerValues.length > 0) {
      const value = getBestAnswerText(answer);
      if (value.trim()) {
        if (section === 'allergy') {
          allergyData.push(`Allergy - ${questionText}: ${value}`);
        } else if (section === 'medication') {
          medicationData.push(`Medication - ${questionText}: ${value}`);
        } else if (section === 'pmh') {
          pmhData.push(`PMH - ${questionText}: ${value}`);
        } else {
          hpiData.push(`${questionText}: ${value}`);
        }
      }
    }

    const optionKeys = getOptionKeys(answer);
    if (optionKeys.length > 0) {
      optionKeys.forEach((key: string, index: number) => {
        if (!key.startsWith('o-')) return;
        const optionText = getOptionText(answer, key, index);
        const noteForKey = answer?.notesForKeys?.[key];
        const hasNote = noteForKey && typeof noteForKey === 'string' && noteForKey.trim();

        if (section === 'allergy') {
          allergyData.push(
            hasNote
              ? `Allergy - ${questionText}: ${optionText} (${noteForKey})`
              : `Allergy - ${questionText}: ${optionText}`,
          );
        } else if (section === 'medication') {
          medicationData.push(
            hasNote
              ? `Medication - ${questionText}: ${optionText} (${noteForKey})`
              : `Medication - ${questionText}: ${optionText}`,
          );
        } else if (section === 'pmh') {
          pmhData.push(
            hasNote
              ? `PMH - ${questionText}: ${optionText} (${noteForKey})`
              : `PMH - ${questionText}: ${optionText}`,
          );
        } else {
          hpiData.push(
            hasNote
              ? `${questionText}: ${optionText} (${noteForKey})`
              : `${questionText}: ${optionText}`,
          );
        }
      });
    }
  });

  return {
    hpiData,
    allergyData,
    pmhData,
    medicationData,
  };
}
